import { useState, useEffect } from 'react';
import { X, Download, ExternalLink, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ChromeIcon = () => (
  <svg viewBox="0 0 48 48" className="w-8 h-8" fill="none">
    <circle cx="24" cy="24" r="22" fill="#fff"/>
    <path d="M24 13.2a10.8 10.8 0 0 0-9.36 5.4L6.6 6.6A22.08 22.08 0 0 1 24 2v11.2z" fill="#EA4335"/>
    <path d="M35.4 18.6H24a10.8 10.8 0 0 0-9.36 5.4l-8.04-13.4A22.08 22.08 0 0 0 2 24c0 5.4 1.96 10.36 5.2 14.2l9.44-16.2h18.76z" fill="#FBBC05"/>
    <path d="M14.64 24a9.36 9.36 0 0 0 4.56 8.04l-8.04 13.92A21.96 21.96 0 0 1 2 24h12.64z" fill="#34A853"/>
    <path d="M24 13.2a10.8 10.8 0 0 1 9.36 5.4h-9.36a10.8 10.8 0 0 0 0 21.6A10.8 10.8 0 0 0 33.36 34.8l8.04 13.92A22.08 22.08 0 0 1 24 46C12.96 46 4 37.04 4 26" fill="#4285F4"/>
    <circle cx="24" cy="24" r="8" fill="#fff"/>
    <circle cx="24" cy="24" r="4.8" fill="#4285F4"/>
  </svg>
);

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = isIOS || (/Safari/i.test(ua) && !/Chrome|CriOS|Chromium/i.test(ua));
  const isChrome = /Chrome/i.test(ua) && !/SamsungBrowser|Edge|Edg|OPR|Opera/i.test(ua);
  const isSamsung = /SamsungBrowser/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  return { isIOS, isSafari, isChrome, isSamsung, isStandalone };
};

const SmartInstallModal = ({ open, onClose, deferredPrompt, onPromptUsed }) => {
  const [browser, setBrowser] = useState({});
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    setBrowser(getBrowserInfo());
  }, [open]);

  if (!open) return null;

  const handleChromeInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setInstalling(false);
      if (onPromptUsed) onPromptUsed();
      if (outcome === 'accepted') onClose();
    }
  };

  const handleOpenInChrome = () => {
    const url = window.location.href;
    const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
    window.location.href = intentUrl;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="install-modal-overlay">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-card border border-border rounded-2xl overflow-hidden animate-slide-up shadow-2xl" data-testid="install-modal">
        {/* Header with gradient */}
        <div className="relative px-6 pt-6 pb-4 text-center">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted/50 transition-colors"
            data-testid="install-modal-close"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* App icon */}
          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 flex items-center justify-center shadow-lg">
            <img src="/rollm8s-logo-dark.png" alt="RollM8s" className="w-12 h-12 object-contain" />
          </div>
          <h2 className="text-lg font-bold text-foreground">Install RollM8s</h2>
          <p className="text-sm text-muted-foreground mt-1">Get the full app experience on your device</p>
        </div>

        <div className="px-6 pb-6">
          {/* Chrome with beforeinstallprompt available */}
          {browser.isChrome && deferredPrompt ? (
            <div data-testid="install-chrome-direct">
              <Button
                onClick={handleChromeInstall}
                disabled={installing}
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 text-base font-semibold gap-2"
                data-testid="install-now-btn"
              >
                <Download className="w-5 h-5" />
                {installing ? 'Installing...' : 'Install Now'}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-3">
                Installs instantly. No app store needed.
              </p>
            </div>
          ) : browser.isIOS ? (
            /* iOS / Safari instructions */
            <div className="space-y-3" data-testid="install-ios-instructions">
              <div className="bg-muted/30 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-primary">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Tap the Share button</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      Look for the <Share className="w-3.5 h-3.5 inline" /> icon at the bottom of your screen
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-primary">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Tap "Add to Home Screen"</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      Scroll down and tap <Plus className="w-3.5 h-3.5 inline" /> Add to Home Screen
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-primary">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Tap "Add"</p>
                    <p className="text-xs text-muted-foreground mt-0.5">RollM8s will appear on your home screen</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Samsung Internet / other browsers - redirect to Chrome */
            <div className="space-y-3" data-testid="install-open-chrome">
              <p className="text-sm text-muted-foreground text-center">
                For the best install experience, open this page in Chrome
              </p>
              <button
                onClick={handleOpenInChrome}
                className="w-full flex items-center justify-center gap-3 h-12 rounded-xl bg-white text-gray-900 font-semibold text-base hover:bg-gray-100 transition-colors"
                data-testid="open-in-chrome-btn"
              >
                <ChromeIcon />
                Open in Chrome
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </button>
              <div className="bg-muted/30 rounded-xl p-3">
                <p className="text-xs text-muted-foreground text-center">
                  Chrome will show an install prompt automatically. Tap <strong>"Install"</strong> to add RollM8s to your home screen.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { SmartInstallModal, getBrowserInfo };
export default SmartInstallModal;
